import React, { useEffect, useState } from 'react';
import { ArrowLeft, ShieldCheck, AlertCircle, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { useTheme } from '../contexts/ThemeContext';
interface OTPScreenProps {
  phoneNumber: string;
  onBack: () => void;
  onVerify: () => void;
}
export function OTPScreen({
  phoneNumber,
  onBack,
  onVerify
}: OTPScreenProps) {
  const {
    isLight
  } = useTheme();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(120);
  const [error, setError] = useState('');
  const [shakeKey, setShakeKey] = useState(0);
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);
  const formatCountdown = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  const handleVerify = () => {
    setIsLoading(true);
    setError('');
    setTimeout(() => {
      setIsLoading(false);
      onVerify();
    }, 1500);
  };
  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');
    // Auto-advance
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }
    // Auto-submit when full
    if (value && index === 5 && newOtp.every(d => d)) {
      setTimeout(() => {
        setIsLoading(true);
        setError('');
        setTimeout(() => {
          setIsLoading(false);
          onVerify();
        }, 1500);
      }, 300);
    }
  };
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };
  return <div className="h-full flex flex-col p-6 relative overflow-hidden" data-id="element-1317">
      {/* Background orbs */}
      <div className={`absolute top-20 -right-16 w-48 h-48 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-vexu/15' : 'bg-vexu/10'}`} data-id="element-1318" />

      {/* Header */}
      <div className="flex items-center mb-8 relative z-10" data-id="element-1319">
        <button onClick={onBack} className={`p-2 -ml-2 rounded-full border transition-colors ${isLight ? 'text-slate-500 hover:text-slate-900 bg-white/60 backdrop-blur-md border-slate-200' : 'text-zinc-400 hover:text-white bg-white/5 backdrop-blur-md border-white/10'}`} data-id="element-1320">
          <ArrowLeft className="w-5 h-5" data-id="element-1321" />
        </button>
      </div>

      <div className="flex-1 flex flex-col pt-4 relative z-10" data-id="element-1322">
        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 border transition-colors duration-500 ${isLight ? 'bg-vexu/10 border-vexu/20 shadow-sm' : 'bg-dark-card border-white/10 shadow-xl shadow-vexu/10'}`} data-id="element-1323">
          <ShieldCheck className={`w-8 h-8 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-1324" />
        </div>

        <h1 className={`text-3xl font-bold mb-3 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1325">
          Verify number
        </h1>
        <p className={`text-base mb-8 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1326">
          Enter the 6-digit code sent to{' '}
          <span className={`font-medium ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1327">
            +1 {phoneNumber}
          </span>
        </p>

        <div className="w-full space-y-6" data-id="element-1328">
          <motion.div key={shakeKey} animate={shakeKey > 0 ? {
          x: [0, -12, 12, -8, 8, -4, 4, 0]
        } : {}} transition={{
          duration: 0.5,
          ease: 'easeInOut'
        }} className={`backdrop-blur-xl border rounded-2xl p-6 transition-colors duration-500 ${error ? isLight ? 'bg-red-50/60 border-red-200/50 shadow-sm' : 'bg-red-500/5 border-red-500/20 shadow-xl shadow-black/50' : isLight ? 'bg-white/60 border-slate-200/50 shadow-sm' : 'bg-dark-card/60 border-white/10 shadow-xl shadow-black/50'}`} data-id="element-1329">
            <div className="flex justify-center gap-3" data-id="element-1330">
              {otp.map((digit, i) => <input key={i} id={`otp-${i}`} type="text" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleOtpChange(i, e.target.value)} onKeyDown={e => handleKeyDown(i, e)} autoFocus={i === 0} className={`w-11 h-14 text-center text-2xl font-bold border rounded-xl outline-none transition-all ${error ? isLight ? 'bg-white border-red-300 text-red-600 focus:border-red-400 focus:ring-4 focus:ring-red-100 shadow-sm' : 'bg-white/5 backdrop-blur-lg border-red-500/30 text-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-500/20' : isLight ? 'bg-white border-slate-200 text-slate-900 focus:border-vexu focus:ring-4 focus:ring-vexu/10 shadow-sm' : 'bg-white/5 backdrop-blur-lg border-white/10 text-white focus:border-vexu focus:ring-2 focus:ring-vexu/20'}`} data-id="element-1331" />)}
            </div>
          </motion.div>

          {/* Error message */}
          <AnimatePresence data-id="element-1332">
            {error && <motion.div initial={{
            opacity: 0,
            y: -8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.25
          }} className={`flex items-center gap-2 px-4 py-3 rounded-xl ${isLight ? 'bg-red-50 border border-red-100' : 'bg-red-500/10 border border-red-500/20'}`} data-id="element-1333">
                <AlertCircle className={`w-4 h-4 flex-shrink-0 ${isLight ? 'text-red-500' : 'text-red-400'}`} data-id="element-1334" />
                <p className={`text-sm font-medium ${isLight ? 'text-red-600' : 'text-red-400'}`} data-id="element-1335">
                  {error}
                </p>
              </motion.div>}
          </AnimatePresence>

          <Button fullWidth size="lg" onClick={handleVerify} loading={isLoading} disabled={otp.some(d => !d)} data-id="element-1336">
            Verify Code
          </Button>

          <div className="text-center space-y-4" data-id="element-1337">
            <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-1338">
              Didn't receive code?{' '}
              {countdown > 0 ? <span className={isLight ? 'text-slate-400' : 'text-zinc-600'} data-id="element-1339">
                  Resend in {formatCountdown(countdown)}
                </span> : <button onClick={() => setCountdown(120)} className={`font-medium transition-colors ${isLight ? 'text-vexu-600 hover:text-vexu-700' : 'text-vexu hover:text-vexu-400'}`} data-id="element-1340">
                  Resend now
                </button>}
            </p>

            <button onClick={onBack} className={`text-sm font-medium transition-colors ${isLight ? 'text-slate-400 hover:text-slate-600' : 'text-zinc-500 hover:text-zinc-300'}`} data-id="element-1341">
              Change phone number
            </button>
          </div>
        </div>
      </div>

      {/* Loading Overlay */}
      <AnimatePresence data-id="element-1342">
        {isLoading && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 z-50 flex flex-col items-center justify-center p-8" data-id="element-1343">
            <div className={`absolute inset-0 ${isLight ? 'bg-white/95' : 'bg-[#0A0A0A]/95'} backdrop-blur-xl`} data-id="element-1344" />
            <div className="relative z-10 flex flex-col items-center text-center w-full max-w-[280px]" data-id="element-1345">
              <div className="relative w-32 h-32 mb-8" data-id="element-1346">
                <motion.div animate={{
              scale: [1, 1.3, 1],
              opacity: [0.3, 0.1, 0.3]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="absolute inset-0 rounded-full border-2 border-vexu/30" data-id="element-1347" />
                <motion.div animate={{
              rotate: 360
            }} transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: 'linear'
            }} className="absolute inset-4 rounded-full border-2 border-transparent border-t-vexu border-r-vexu/30" data-id="element-1348" />
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-1349">
                  <motion.div animate={{
                scale: [1, 1.1, 1]
              }} transition={{
                duration: 1.5,
                repeat: Infinity
              }} data-id="element-1350">
                    <ShieldCheck className="w-10 h-10 text-vexu" data-id="element-1351" />
                  </motion.div>
                </div>
              </div>
              <p className={`text-base font-semibold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1352">
                Verifying Code
              </p>
              <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1353">
                Checking your verification code...
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}